import React, { useEffect, useState } from "react";
import { Input, Checkbox } from "antd";
import { observer } from "mobx-react";
import classNames from "classnames";
import BaseModal from "@app/components/modal";
import Loader from "@app/components/loader/content";

import chatStore from "../../state/store/ai-chat";

import "./style/source-docs.scoped.scss";

const SourceDocsPicker = observer(({ files, onCancel, onSelect }) => {
    const [documents, setDocuments] = useState(null);
    const [filtered, setFiltered] = useState([]);
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState({});

    const selectAllState = React.useMemo(() => {
        if (filtered.length === 0) {
            return { checked: false, indeterminate: false };
        }

        const selectedCount = filtered.filter((file) => selected[file._id]).length;

        if (selectedCount === 0) {
            return { checked: false, indeterminate: false };
        }

        if (selectedCount === filtered.length) {
            return { checked: true, indeterminate: false };
        }

        return { checked: false, indeterminate: true };
    }, [filtered, selected]);

    function onSelectAllToggle() {
        const newSelected = { ...selected };
        const shouldSelect = !selectAllState.checked;

        filtered.forEach((file) => {
            newSelected[file._id] = shouldSelect;
        });

        setSelected(newSelected);
    }

    useEffect(() => {
        const selected = {};

        files.map((file) => {
            selected[file._id] = true;
        });

        setSelected({ ...selected });
    }, [documents]);

    useEffect(() => {
        const filtered = filterDocuments();
        setFiltered(filtered);
    }, [documents, filter]);

    /**
     * Filter the list of documents
     */
    function filterDocuments() {
        const tokens = filter
            .split(/\s+/g)
            .filter((token) => !!token)
            .map((token) => token.toLowerCase());

        if (tokens.length === 0) {
            return documents ?? [];
        }

        return documents.filter((entry) => {
            const name = entry.name?.toLowerCase() || "";

            return tokens.every((token) => {
                return name.includes(token);
            });
        });
    }

    /**
     * Build the list of usable documents
     */
    async function load() {
        const documents = await chatStore.loadSourceDocs();
        const usable = documents
            .map((doc) => {
                const fileId = doc.getPdfFileId;
                if (fileId) {
                    return {
                        _id: fileId,
                        documentId: doc._id,
                        name: doc.name,
                    };
                }
            })
            .filter((doc) => !!doc);

        setDocuments(usable);
    }

    /**
     * Handle selection toggle
     */
    function onToggle(fileId) {
        selected[fileId] = !selected[fileId];
        setSelected({ ...selected });
    }

    /**
     * Convert the selection object into a list of selected files
     */
    function handleSelection() {
        const files = documents
            .filter((doc) => selected[doc._id])
            .map((doc) => ({
                ...doc,
                type: "file",
            }));

        onSelect(files);
    }

    useEffect(() => {
        load();
    }, []);

    return (
        <BaseModal
            title="Pick Source Documents"
            visible={true}
            onCancel={onCancel}
            onOk={handleSelection}
            okText="Select"
            width={600}
            height="50vh"
            className={classNames("source-docs-picker")}
        >
            <div className="container">
                {documents && (
                    <>
                        <div className="search">
                            <Input.Search
                                placeholder="Search"
                                onSearch={(filter) => setFilter(filter)}
                                allowClear
                            />
                        </div>

                        {filtered.length > 0 && (
                            <div className="select-all" onClick={onSelectAllToggle}>
                                <div className="checkbox">
                                    <Checkbox
                                        checked={selectAllState.checked}
                                        indeterminate={selectAllState.indeterminate}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={onSelectAllToggle}
                                    />
                                </div>
                                <div className="label">
                                    Select All ({filtered.length} document
                                    {filtered.length !== 1 ? "s" : ""})
                                </div>
                            </div>
                        )}

                        <div className="files">
                            {filtered.map((file) => {
                                return (
                                    <div
                                        className="document"
                                        key={file._id}
                                        onClick={() => onToggle(file._id)}
                                    >
                                        <div className="checkbox">
                                            <Checkbox
                                                checked={selected[file._id]}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    onToggle(file._id);
                                                }}
                                            />
                                        </div>
                                        <div className="name">{file.name}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {!documents && (
                    <div className="loading">
                        <Loader loading={true} />
                    </div>
                )}
            </div>
        </BaseModal>
    );
});

export default SourceDocsPicker;
